import {DragItemType, GridItemType} from "../types";
import {eventEmitter} from "../EventEmitter";
import {Lang, ModeTypes, ScreenTypes} from "../provider/AppProvider";

export interface DataStorage {
    grid_data: GridItemType[][] | undefined;
    tools_panel: DragItemType[] | undefined;
    score_data: number;
    total_score_data: number;
    video_watch_data: boolean;
    hint_was_showing: boolean;
}

export interface DataStorageMode {
    normal: DataStorage;
    bomb: DataStorage;
}

export interface DataGameStorage extends DataStorageMode {
    language: Lang;
    screen: ScreenTypes;
    mode: ModeTypes;
    sound: boolean;
}

class StorageService<T> {
    private data_mode: DataGameStorage = {
        language: '',
        screen: 'start',
        mode: 'normal',
        sound: false,

        normal: {
            grid_data: undefined,
            score_data: 0,
            tools_panel: undefined,
            total_score_data: 0,
            video_watch_data: false,
            hint_was_showing: false,
        },
        bomb: {
            grid_data: undefined,
            score_data: 0,
            tools_panel: undefined,
            total_score_data: 0,
            video_watch_data: false,
            hint_was_showing: false,
        }
    }

    private storage: Storage;
    private readonly key: string;
    private readonly isProduction: boolean

    constructor(key: string, isProduction: boolean, storage: Storage = localStorage) {
        this.key = key;
        this.storage = storage;
        this.isProduction = isProduction;
    }

    setDataFromStorage(data: DataGameStorage): void {
        if (!data.mode) {
            const normalData: DataStorageMode['normal'] = (data as unknown as DataStorageMode['normal']);
            const toolsPanelOptimizer = normalData.tools_panel?.map((item, index) => ({...item, indexToolPanel: index}));
            this.data_mode = {
                language: data.language,
                screen: data.screen,
                mode: 'normal',
                sound: false,
                normal: {...normalData, tools_panel: toolsPanelOptimizer},
                bomb: {
                    grid_data: undefined,
                    score_data: 0,
                    tools_panel: undefined,
                    total_score_data: 0,
                    video_watch_data: false,
                    hint_was_showing: false,
                },
            }
            return;
        }
        this.data_mode = data;
    }

    setDataInitialize(data: DataGameStorage): void {
        this.setDataFromStorage(data);
    }

    public get isBombMode(): boolean {
        return this.data_mode.mode === 'bomb';
    }

    setItemApp(data: Partial<DataGameStorage>): void {
        if (this.isProduction) {
            const formattedData = {...this.data_mode, ...data};

            this.data_mode = formattedData;
            eventEmitter.emit('data_update', formattedData); // Отправляем событие data_update
        } else {
            try {
                const formattedData = {...this.data_mode, ...data};
                this.data_mode = formattedData;
                this.storage.setItem(this.key, JSON.stringify(formattedData));
                eventEmitter.emit('data_update', formattedData); // Отправляем событие data_update
            } catch (error) {
                console.error("Error saving to storage", error);
            }
        }
    }

    // Save data to storage
    setItem(data: Partial<DataStorage>): void {
        if (this.isProduction) {
            const fieldData = this.data_mode.mode === 'normal' ? 'normal' : 'bomb';
            const formattedData = {...this.data_mode, [fieldData]: {...this.data_mode[fieldData], ...data}};

            this.data_mode = formattedData;
            eventEmitter.emit('data_update', formattedData); // Отправляем событие data_update
        } else {
            try {
                const fieldData = this.data_mode.mode === 'normal' ? 'normal' : 'bomb';
                const formattedData = {...this.data_mode, [fieldData]: {...this.data_mode[fieldData], ...data}};

                this.data_mode = formattedData;
                this.storage.setItem(this.key, JSON.stringify(formattedData));
                eventEmitter.emit('data_update', formattedData); // Отправляем событие data_update
            } catch (error) {
                console.error("Error saving to storage", error);
            }
        }
    }

    getItemApp(): DataGameStorage | null {
        if (this.isProduction) {
            return this.data_mode;
        } else {
            try {
                const serializedValue = this.storage.getItem('data');
                if (serializedValue === null) {
                    return null;
                }

                this.setDataFromStorage(JSON.parse(serializedValue));

                return this.data_mode;
            } catch (error) {
                console.error("Error reading from storage", error);
                return null;
            }
        }
    }

    getItem(): DataStorage | null {
        if (this.isProduction) {
            const fieldData = this.data_mode.mode === 'normal' ? 'normal' : 'bomb';
            return this.data_mode[fieldData];
        } else {
            try {
                const fieldData = this.data_mode.mode === 'normal' ? 'normal' : 'bomb';
                const serializedValue = this.storage.getItem('data');
                if (serializedValue === null) {
                    return null;
                }

                this.setDataFromStorage(JSON.parse(serializedValue));

                return this.data_mode[fieldData];
            } catch (error) {
                console.error("Error reading from storage", error);
                return null;
            }
        }
    }
}

export const dataStorage = new StorageService<DataStorage>('data', true);

